import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Alert,
  AlertDescription,
  Progress,
  Card,
  CardContent,
  Badge,
} from './components/ui';
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  Upload,
  FileSpreadsheet,
  ArrowRight,
} from 'lucide-react';
import ConflictResolutionPanel from './ConflictResolutionPanel';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const OutboundImportModal = ({
  show,
  onClose,
  currentShipmentId,
  currentShipmentItems,
  currentTeamId,
  servicesData,
  onImportComplete,
  showSnackbar,
}) => {
  // State management
  const [step, setStep] = useState('input'); // input, loading, conflicts, preview, processing, complete
  const [outboundIdsInput, setOutboundIdsInput] = useState('');
  const [sourceOutbounds, setSourceOutbounds] = useState([]);
  const [failedOutbounds, setFailedOutbounds] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [mergedData, setMergedData] = useState([]);
  const [processingProgress, setProcessingProgress] = useState({});
  const [error, setError] = useState(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setStep('input');
      setOutboundIdsInput('');
      setSourceOutbounds([]);
      setFailedOutbounds([]);
      setConflicts([]);
      setMergedData([]);
      setProcessingProgress({});
      setError(null);
    }
  }, [show]);

  // Parse outbound IDs from input
  const parseOutboundIds = (input) => {
    return input
      .split(/[\n,\s]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0);
  };

  // Fetch data from multiple outbounds
  const handleFetchOutbounds = async () => {
    const ids = parseOutboundIds(outboundIdsInput);

    if (ids.length === 0) {
      setError('Please enter at least one outbound ID');
      return;
    }

    if (ids.includes(currentShipmentId)) {
      setError('Cannot import from the currently open outbound');
      return;
    }

    setStep('loading');
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/bulk_fetch_outbound_items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outbound_ids: ids,
          current_team_id: currentTeamId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch outbound data');
      }

      const data = await response.json();

      setSourceOutbounds(data.outbounds || []);
      setFailedOutbounds(data.failed || []);

      if (data.outbounds.length === 0) {
        setError('No valid outbound data found. Please check the IDs and try again.');
        setStep('input');
        return;
      }

      // Detect conflicts and merge data
      detectConflictsAndMerge(data.outbounds);

    } catch (error) {
      console.error('Error fetching outbound data:', error);
      setError(error.message || 'Failed to fetch outbound data');
      setStep('input');
    }
  };

  // Detect conflicts across source outbounds
  const detectConflictsAndMerge = (outbounds) => {
    const skuMap = new Map();
    const detectedConflicts = [];

    // Build SKU map with all occurrences
    outbounds.forEach((outbound) => {
      outbound.items.forEach((item) => {
        const sku = item.item.merchant_sku;

        if (!skuMap.has(sku)) {
          skuMap.set(sku, []);
        }

        skuMap.get(sku).push({
          outbound_id: outbound.outbound_id,
          outbound_name: outbound.outbound_name,
          item: item,
          services: item.service_lines?.map(sl => ({
            id: sl.service.id,
            name: sl.service.name,
            charge: sl.service.charge,
            quantity: sl.quantity || 1,
          })) || [],
          expiry_date: item.expiry_date,
          quantity: item.quantity,
        });
      });
    });

    // Check for conflicts
    skuMap.forEach((sources, sku) => {
      if (sources.length > 1) {
        // Check for service conflicts
        const allServices = sources.flatMap(s => s.services.map(srv => srv.name));
        const uniqueServices = new Set(allServices);
        const firstServices = new Set(sources[0].services.map(s => s.name));

        if (uniqueServices.size > firstServices.size ||
            sources.some(s => {
              const sNames = new Set(s.services.map(srv => srv.name));
              return sNames.size !== firstServices.size ||
                     ![...sNames].every(name => firstServices.has(name));
            })) {
          detectedConflicts.push({
            sku,
            type: 'services',
            sources: sources.map(s => ({
              outbound_id: s.outbound_id,
              outbound_name: s.outbound_name,
              value: s.services,
            })),
            resolution: null,
          });
        }

        // Check for expiry date conflicts
        const uniqueDates = new Set(
          sources.map(s => s.expiry_date).filter(date => date != null)
        );
        if (uniqueDates.size > 1) {
          detectedConflicts.push({
            sku,
            type: 'expiry_date',
            sources: sources.map(s => ({
              outbound_id: s.outbound_id,
              outbound_name: s.outbound_name,
              value: s.expiry_date,
            })),
            resolution: null,
          });
        }
      }
    });

    setConflicts(detectedConflicts);

    if (detectedConflicts.length > 0) {
      setStep('conflicts');
    } else {
      // No conflicts, proceed to merge
      const merged = mergeDataWithoutConflicts(skuMap);
      setMergedData(merged);
      setStep('preview');
    }
  };

  // Merge data when no conflicts exist
  const mergeDataWithoutConflicts = (skuMap) => {
    const merged = [];

    skuMap.forEach((sources, sku) => {
      const firstSource = sources[0];
      const totalQuantity = sources.reduce((sum, s) => sum + s.quantity, 0);

      merged.push({
        sku,
        item: firstSource.item.item,
        quantity: totalQuantity,
        expiry_date: firstSource.expiry_date,
        services: firstSource.services,
        source_outbounds: sources.map(s => ({
          id: s.outbound_id,
          name: s.outbound_name,
          quantity: s.quantity,
        })),
      });
    });

    return merged;
  };

  // Handle conflict resolutions
  const handleConflictsResolved = (resolvedConflicts) => {
    // Rebuild merged data with resolutions
    const skuMap = new Map();

    sourceOutbounds.forEach((outbound) => {
      outbound.items.forEach((item) => {
        const sku = item.item.merchant_sku;

        if (!skuMap.has(sku)) {
          skuMap.set(sku, []);
        }

        skuMap.get(sku).push({
          outbound_id: outbound.outbound_id,
          outbound_name: outbound.outbound_name,
          item: item,
          services: item.service_lines?.map(sl => ({
            id: sl.service.id,
            name: sl.service.name,
            charge: sl.service.charge,
            quantity: sl.quantity || 1,
          })) || [],
          expiry_date: item.expiry_date,
          quantity: item.quantity,
        });
      });
    });

    const merged = [];

    skuMap.forEach((sources, sku) => {
      const totalQuantity = sources.reduce((sum, s) => sum + s.quantity, 0);
      const firstSource = sources[0];

      // Apply conflict resolutions
      let finalServices = firstSource.services;
      let finalExpiryDate = firstSource.expiry_date;

      resolvedConflicts.forEach((conflict) => {
        if (conflict.sku === sku && conflict.resolution) {
          if (conflict.type === 'services') {
            finalServices = conflict.resolution;
          } else if (conflict.type === 'expiry_date') {
            finalExpiryDate = conflict.resolution;
          }
        }
      });

      merged.push({
        sku,
        item: firstSource.item.item,
        quantity: totalQuantity,
        expiry_date: finalExpiryDate,
        services: finalServices,
        source_outbounds: sources.map(s => ({
          id: s.outbound_id,
          name: s.outbound_name,
          quantity: s.quantity,
        })),
      });
    });

    setMergedData(merged);
    setStep('preview');
  };

  // Apply merged data to current outbound
  const handleConfirmImport = async () => {
    setStep('processing');
    setProcessingProgress({});

    try {
      // Step 1: Filter to only items that exist in current outbound
      setProcessingProgress({ items: 'Checking items...' });

      // Only process items that already exist in the current outbound
      const itemsToProcess = mergedData.filter(mergedItem => {
        return currentShipmentItems.some(
          si => si.item.merchant_sku === mergedItem.sku
        );
      });

      const skippedItems = mergedData.filter(mergedItem => {
        return !currentShipmentItems.some(
          si => si.item.merchant_sku === mergedItem.sku
        );
      });

      if (skippedItems.length > 0) {
        console.log(`Skipping ${skippedItems.length} items that don't exist in current outbound:`,
          skippedItems.map(item => item.sku));
      }

      setProcessingProgress({
        items: `Processing ${itemsToProcess.length} matching items (${skippedItems.length} skipped)`,
        itemsComplete: true
      });

      // Step 2: Update expiry dates (only for items that exist)
      setProcessingProgress(prev => ({ ...prev, expiry: 'Updating expiry dates...' }));

      const itemsWithExpiry = itemsToProcess.filter(item => item.expiry_date);

      for (const item of itemsWithExpiry) {
        const matchedItem = currentShipmentItems.find(
          si => si.item.merchant_sku === item.sku
        );

        if (matchedItem) {
          const updateResponse = await fetch(`${API_URL}/api/update-expiry`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              itemId: matchedItem.id,
              shipmentId: currentShipmentId,
              item: { ...matchedItem, expiry_date: item.expiry_date },
            }),
          });

          if (!updateResponse.ok) {
            console.error(`Failed to update expiry for ${item.sku}`);
          }
        }
      }

      setProcessingProgress(prev => ({ ...prev, expiry: 'Expiry dates updated', expiryComplete: true }));

      // Step 3: Apply services (only for items that exist)
      setProcessingProgress(prev => ({ ...prev, services: 'Applying services...' }));

      const itemsWithServices = itemsToProcess.filter(item => item.services && item.services.length > 0);

      for (const item of itemsWithServices) {
        const matchedItem = currentShipmentItems.find(
          si => si.item.merchant_sku === item.sku
        );

        if (matchedItem && item.services.length > 0) {
          const servicesPayload = item.services.reduce((acc, service, index) => {
            acc[index] = {
              id: service.id,
              name: service.name,
              charge: service.charge,
              quantity: service.quantity || 1,
            };
            return acc;
          }, {});

          const updateResponse = await fetch(
            `https://app.yyzprep.ca/api/shipments/outbound/${currentShipmentId}/outbound-shipment-item/${matchedItem.id}/services?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ services: servicesPayload }),
            }
          );

          if (!updateResponse.ok) {
            console.error(`Failed to apply services for ${item.sku}`);
          }
        }
      }

      setProcessingProgress(prev => ({ ...prev, services: 'Services applied', servicesComplete: true }));

      // Step 4: Complete
      setStep('complete');

      // Call the completion callback after a brief delay
      setTimeout(() => {
        onImportComplete?.();
        showSnackbar?.('Sync completed successfully - expiry dates and services updated for matching items', 'success');
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error during import:', error);
      setError(error.message || 'Failed to import data');
      setStep('preview');
    }
  };

  return (
    <Dialog open={show} onOpenChange={onClose}>
      <DialogContent className="!w-[80vw] !max-w-[80vw] !h-[80vh] !max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Upload className="w-6 h-6" />
            Sync from Outbound IDs
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            Sync expiry dates and services from other outbounds for matching SKUs only
          </p>
        </DialogHeader>

        {/* Input Step */}
        {step === 'input' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Enter Outbound IDs (comma or line-separated)
              </label>
              <textarea
                value={outboundIdsInput}
                onChange={(e) => setOutboundIdsInput(e.target.value)}
                placeholder="e.g., 12345, 67890&#10;or one per line"
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Maximum 20 outbound IDs. You can separate them with commas, spaces, or new lines.
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleFetchOutbounds} disabled={!outboundIdsInput.trim()}>
                Fetch Data
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Loading Step */}
        {step === 'loading' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
            <p className="text-lg font-medium">Fetching outbound data...</p>
            <p className="text-sm text-gray-500">This may take a moment</p>
          </div>
        )}

        {/* Conflicts Step */}
        {step === 'conflicts' && (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Conflicts detected! Please resolve the conflicts below before proceeding.
              </AlertDescription>
            </Alert>

            <ConflictResolutionPanel
              conflicts={conflicts}
              onResolve={handleConflictsResolved}
              onCancel={() => setStep('input')}
            />
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-2">Sync Summary</h3>
              <p className="text-sm text-gray-700 mb-3">
                Only items that <strong>already exist</strong> in the current outbound will have their expiry dates and services updated. New items will NOT be added.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Source Outbounds:</span>
                  <span className="ml-2 font-medium">{sourceOutbounds.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Items to Sync:</span>
                  <span className="ml-2 font-medium text-green-600">
                    {mergedData.filter(item => currentShipmentItems.some(si => si.item.merchant_sku === item.sku)).length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Items to Skip:</span>
                  <span className="ml-2 font-medium text-orange-600">
                    {mergedData.filter(item => !currentShipmentItems.some(si => si.item.merchant_sku === item.sku)).length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Items with Services:</span>
                  <span className="ml-2 font-medium">
                    {mergedData.filter(item => item.services && item.services.length > 0 && currentShipmentItems.some(si => si.item.merchant_sku === item.sku)).length}
                  </span>
                </div>
              </div>
            </div>

            {failedOutbounds.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Failed to fetch {failedOutbounds.length} outbound(s):{' '}
                  {failedOutbounds.map(f => `${f.outbound_id} (${f.error})`).join(', ')}
                </AlertDescription>
              </Alert>
            )}

            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">SKU</th>
                    <th className="px-4 py-2 text-left">Title</th>
                    <th className="px-4 py-2 text-left">Services</th>
                    <th className="px-4 py-2 text-left">Expiry Date</th>
                  </tr>
                </thead>
                <tbody>
                  {mergedData.map((item, idx) => {
                    const willSync = currentShipmentItems.some(si => si.item.merchant_sku === item.sku);
                    return (
                      <tr key={idx} className={`border-t ${willSync ? 'hover:bg-green-50' : 'bg-gray-50 opacity-60'}`}>
                        <td className="px-4 py-2">
                          {willSync ? (
                            <Badge variant="default" className="bg-green-600 text-xs">
                              Will Sync
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">
                              Skip
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{item.sku}</td>
                        <td className="px-4 py-2 text-xs">{item.item.title}</td>
                        <td className="px-4 py-2">
                          {item.services?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {item.services.map((srv, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {srv.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">None</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {item.expiry_date || <span className="text-gray-400">N/A</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('input')}>
                Back
              </Button>
              <Button onClick={handleConfirmImport}>
                Confirm Import
                <CheckCircle2 className="ml-2 w-4 h-4" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Processing Step */}
        {step === 'processing' && (
          <div className="space-y-6 py-8">
            <div className="flex flex-col items-center space-y-2">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
              <p className="text-lg font-medium">Processing import...</p>
            </div>

            <div className="space-y-3">
              {processingProgress.items && (
                <div className="flex items-center gap-2">
                  {processingProgress.itemsComplete ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  )}
                  <span>{processingProgress.items}</span>
                </div>
              )}

              {processingProgress.expiry && (
                <div className="flex items-center gap-2">
                  {processingProgress.expiryComplete ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  )}
                  <span>{processingProgress.expiry}</span>
                </div>
              )}

              {processingProgress.services && (
                <div className="flex items-center gap-2">
                  {processingProgress.servicesComplete ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  )}
                  <span>{processingProgress.services}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <p className="text-xl font-semibold text-green-600">Sync Completed!</p>
            <p className="text-sm text-gray-600">
              Successfully synced expiry dates and services for matching items from {sourceOutbounds.length} outbound(s)
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OutboundImportModal;
